import {
  Button,
  Checkbox,
  FamilyChildProfile,
  FamilyParentProfile,
  JoonUIColor,
  ModalHeader,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useEffect } from "react"

import { useDeleteQuestModalStore } from "./DeleteQuestModal"
import { QuestModalView, useQuestModalStore } from "./useQuestModalStore"
import { UserQuestStatus } from "./UserQuestStatus"
import AddAttachmentButton from "../../../components/attachments/AddAttachmentButton"
import AttachmentItem from "../../../components/attachments/AttachmentItem"
import DateInput from "../../../components/dateInput/DateInput"
import CustomTextArea from "../../../components/textarea/TextArea"
import TimeInputNew from "../../../components/timeInput/TimeInputNew"
import { useThrottle } from "../../../hooks/useThrottle"
import { useFamilyQuery } from "../../../networking/families/queries"
import {
  useAddQuestMutation,
  useEditQuestMutation,
} from "../../../networking/quests/mutations"
import { createResource } from "../../../networking/resources/api"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import { convertTimeToDatetimeString } from "../../../util/time"

const AddEditQuest = () => {
  const {
    onClose,
    title,
    setTitle,
    description,
    setDescription,
    time,
    setTime,
    date,
    setDate,
    selectedUserIds,
    setSelectedUserIds,
    view,
    questGroup,
    error,
    setError,
    attachment,
    setAttachment,
  } = useQuestModalStore()
  const { onOpen: openDeleteModal } = useDeleteQuestModalStore()

  const { data: family } = useFamilyQuery()
  const addQuestMutation = useAddQuestMutation()
  const editQuestMutation = useEditQuestMutation()

  // select first user in list by default
  useEffect(() => {
    if (family?.profiles.length && selectedUserIds.length === 0) {
      setSelectedUserIds([family.profiles[0].user.id])
    }
  }, [family])

  const isEditing = view === QuestModalView.EDIT

  const handleAddQuest = useThrottle(async () => {
    if (!title) return setError("Please enter a title")
    if (!description) return setError("Please enter a description")
    if (!date || !time) return setError("Please select a date and time")
    if (selectedUserIds.length === 0)
      return setError("Please select a participant")

    let attachmentId: number | undefined
    if (attachment) {
      const formData = new FormData()
      formData.append("resource_url", attachment)
      formData.append("description", attachment.type)
      formData.append("title", attachment.name)
      const resource = await createResource(formData)
      attachmentId = resource.id
    }

    await addQuestMutation.mutateAsync({
      questInfo: {
        title,
        description,
        time: dayjs(convertTimeToDatetimeString(time)).format("h:mm A"),
        date: date.format("YYYY-MM-DD"),
        resourceId: attachmentId,
      },
      userIds: selectedUserIds,
    })
    trackAnalyticEvent(ANALYTIC_EVENTS.ADD_QUEST)
    onClose()
  }, 1000)

  const handleEditQuest = useThrottle(async () => {
    if (!title) return setError("Please enter a title")
    if (!description) return setError("Please enter a description")
    if (!date || !time) return setError("Please select a date and time")
    if (selectedUserIds.length === 0)
      return setError("Please select a participant")

    const seriesId = questGroup?.[0]?.series.id
    if (!seriesId) return setError("Task not found")

    let attachmentId: number | null | undefined
    if (attachment) {
      const formData = new FormData()
      formData.append("resource_url", attachment)
      formData.append("description", attachment.type)
      formData.append("title", attachment.name)

      const resource = await createResource(formData)
      attachmentId = resource.id
    } else if (attachment === null) {
      attachmentId = null
    }

    await editQuestMutation.mutateAsync({
      questInfo: {
        seriesId: seriesId,
        title,
        description,
        time: dayjs(convertTimeToDatetimeString(time)).format("h:mm A"),
        date: date.format("YYYY-MM-DD"),
        resourceId: attachmentId,
      },
      userIds: selectedUserIds,
    })
    onClose()
  }, 1000)

  return (
    <>
      <ModalHeader
        title={isEditing ? "Edit task" : "Add task"}
        onClose={onClose}
        showBorderBottom
      />
      <div className="flex-flex-col overflow-y-auto">
        <div className="flex flex-col p-6 pt-4 gap-4">
          <CustomTextArea
            value={title}
            onChange={(e: any) => setTitle(e.target.value)}
            placeholder="Task title"
            defaultRows={1}
            style={{
              fontSize: "1.25rem",
              lineHeight: "1.5rem",
              fontWeight: 500,
              border: "none",
              outline: "none",
              padding: "0px",
            }}
            maxLength={100}
          />
          <CustomTextArea
            value={description}
            onChange={(e: any) => setDescription(e.target.value)}
            placeholder="Helpful description or instructions"
            defaultRows={3}
            style={{
              fontSize: "1rem",
              lineHeight: "1.25rem",
              fontWeight: 400,
              border: "none",
              outline: "none",
              padding: "0px",
            }}
            maxLength={500}
          />
          {attachment ? (
            <AttachmentItem
              key={attachment.name}
              name={attachment.name}
              url={URL.createObjectURL(attachment)}
              onRemove={() => setAttachment(undefined)}
            />
          ) : attachment === null ? (
            <AddAttachmentButton
              attachments={attachment ? [attachment] : []}
              setAttachments={(files) => setAttachment(files[0])}
            />
          ) : questGroup?.[0]?.series.resource ? (
            <AttachmentItem
              name={questGroup?.[0]?.series.resource.title}
              url={questGroup?.[0]?.series.resource.resource_url}
              onRemove={() => setAttachment(null)} // delete resource
            />
          ) : (
            <AddAttachmentButton
              attachments={attachment ? [attachment] : []}
              setAttachments={(files) => setAttachment(files[0])}
            />
          )}
        </div>
        {view === QuestModalView.ADD ? (
          <div className="flex flex-col gap-2 border-t border-gray-200 p-6 pt-4">
            <Typography variant="body">Select participant:</Typography>
            {family?.profiles.map((profile) => (
              <UserCheckbox key={profile.user.id} profile={profile} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-2 p-6 pt-4 border-t border-gray-200">
            {family?.profiles.map((profile) => (
              <UserQuestStatus
                key={profile.user.id}
                profile={profile}
                questGroup={questGroup}
              />
            ))}
          </div>
        )}
        <div className="flex flex-col gap-2 p-6 pt-4 border-t border-gray-200">
          <div className="flex flex-col">
            <Typography variant="body">Schedule for:</Typography>
            <Typography variant="bodySmall">
              You can schedule for up to 14 days from today
            </Typography>
          </div>
          <div className="flex flex-row gap-2 items-center">
            <DateInput date={date} setDate={setDate} />
            <TimeInputNew value={time || ""} setValue={setTime} />
            <Typography variant="bodySmall">(client local time)</Typography>
          </div>
        </div>
      </div>
      {error && (
        <div className="flex flex-row w-full p-6 pt-2 pb-0 gap-4 justify-between items-center">
          <Typography
            variant="bodySmall"
            textAlign="center"
            color={JoonUIColor.semantic.destructive}
          >
            {error}
          </Typography>
        </div>
      )}
      <div className="flex flex-row w-full p-6 py-3 gap-4 justify-between items-center">
        {isEditing && (
          <TextButton onClick={openDeleteModal}>
            <Typography variant="body" color={JoonUIColor.semantic.destructive}>
              Delete
            </Typography>
          </TextButton>
        )}
        <div className="flex flex-row gap-4 w-full justify-end">
          {isEditing && (
            <TextButton onClick={onClose}>
              <Typography variant="body" color={JoonUIColor.text.primaryAccent}>
                Cancel
              </Typography>
            </TextButton>
          )}
          <Button
            text={isEditing ? "Save changes" : "Create task"}
            onClick={isEditing ? handleEditQuest : handleAddQuest}
            fullWidth={!isEditing}
            isLoading={
              addQuestMutation.isLoading || editQuestMutation.isLoading
            }
            style={{ minWidth: "200px" }}
          />
        </div>
      </div>
    </>
  )
}

export default AddEditQuest

const UserCheckbox = ({
  profile,
}: {
  profile: FamilyChildProfile | FamilyParentProfile
}) => {
  const { selectedUserIds, setSelectedUserIds } = useQuestModalStore()

  const toggleSelectedUser = () => {
    if (selectedUserIds.includes(profile.user.id)) {
      setSelectedUserIds([])
    } else {
      setSelectedUserIds([profile.user.id])
    }
  }

  return (
    <Checkbox
      selected={selectedUserIds.includes(profile.user.id)}
      onChange={toggleSelectedUser}
      name={profile.user.name}
      label={`${profile.user.name}${
        profile.nickname ? ` (${profile.nickname})` : ""
      }`}
      fullWidth
    />
  )
}
